import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ScenarioCode } from '@app/core/enums/scenario-code';
import { ScenarioInterface } from '@app/core/interfaces';
import { ScenariosSelectorService } from '@app/store/my-zone/scenarios/scenarios-selector.service';
import {
    DateService,
    RelationDataService,
    RelationInterface,
    PurchasedProductInterface,
    InsurancePolicyDataService,
    PolicyValidityStatusCodeEnum,
} from 'outshared-lib';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, pairwise } from 'rxjs/operators';
import { NotificationBannerIdEnum } from './notification-banner-id.enum';
import { CookieStorage, StorageService } from '@inshared/shared/util-storage';

const CLAIM_YEAR_KEY = 'Is.sjvg';
const TRANSFER_KEY = 'Is.overstap';

@Component({
    selector: 'ins-notification-banner-container',
    template: `
        <ins-notification-banner
            [purchasedProducts]="purchasedProducts"
            [notificationBanner]="notificationBanner$ | async"
            [notifications]="notifications$ | async"
            (toggle)="toggle($event)"
        >
        </ins-notification-banner>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBannerContainer implements OnInit, OnDestroy {
    public purchasedProducts: PurchasedProductInterface[];
    private notificationBannerSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public notificationBanner$: Observable<boolean> = this.notificationBannerSubject$.asObservable();
    private notificationsSubject$: BehaviorSubject<{ id: NotificationBannerIdEnum; rank: number }[]> =
        new BehaviorSubject([]);
    public notifications$: Observable<{ id: NotificationBannerIdEnum; rank: number }[]> = this.notificationsSubject$
        .asObservable()
        .pipe(
            pairwise(),
            map(([previous, next]) => [...previous, ...next]),
            map((notifications) => this.filterDuplicateNotifications(notifications)),
            map((notifications) => this.sortNotifications(notifications))
        );
    private scenarios$: Observable<ScenarioInterface[]> = this.scenariosSelectorService.getAllScenarios$.pipe(
        map((scenarios) => scenarios.filter((scenario) => scenario.scenario_code === ScenarioCode.DamageFreeYears))
    );
    private subscription: Subscription = new Subscription();
    private expireDate: string = this.dateService.add(3, 'day');
    private relation$ = this.relationDataService.getRelation$();

    constructor(
        private insurancePolicyDataService: InsurancePolicyDataService,
        private relationDataService: RelationDataService,
        private scenariosSelectorService: ScenariosSelectorService,
        @Inject(StorageService) private storage: CookieStorage,
        private readonly dateService: DateService
    ) {}

    public get isClaimYears(): boolean {
        return this.storage.getObject(CLAIM_YEAR_KEY) !== 1;
    }

    public get isTransfer(): boolean {
        return this.storage.getObject(TRANSFER_KEY) !== '1';
    }

    public ngOnInit(): void {
        this.getPurchasedProducts();
        this.getScenariosProducts();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public toggle($event: boolean): void {
        this.notificationBannerSubject$.next($event);
    }

    private getPurchasedProducts(): void {
        const getPurchasedProducts = this.insurancePolicyDataService
            .getPurchasedProducts$()
            .pipe(
                map((purchasedProducts) =>
                    purchasedProducts.filter(
                        (purchasedProduct) =>
                            purchasedProduct.product.validityStatusCode === PolicyValidityStatusCodeEnum.Transfer
                    )
                )
            )
            .subscribe((purchasedProducts) => (this.purchasedProducts = purchasedProducts));

        this.subscription.add(getPurchasedProducts);
    }

    private getScenariosProducts(): void {
        const scenariosProducts$ = combineLatest([
            this.scenarios$,
            this.insurancePolicyDataService.getPurchasedProducts$(),
        ]).subscribe(([scenarios, purchasedProducts]) => this.validateScenarios(scenarios, purchasedProducts));

        this.subscription.add(scenariosProducts$);
    }

    private validateScenarios(scenarios: ScenarioInterface[], purchasedProducts: PurchasedProductInterface[]): void {
        this.validateTransferCookie(scenarios);
        this.validateTransferPurchasedProducts(scenarios, purchasedProducts);
        this.validateClaimsYearsDocument(scenarios);
        this.validateClaimsYearsCoverage(scenarios);
        this.validateClientAnniversary();
    }

    private validateClientAnniversary(): void {
        const subscriptionRef = this.relation$.subscribe((relation: RelationInterface) => {
            const now = this.dateService.now();
            const activationDate = relation?.activation_timestamp;
            const timePassedInMonths = now.diff(activationDate, 'month');

            // if user is a customer for at least one year
            if (timePassedInMonths < 24 && timePassedInMonths >= 12) {
                this.notificationsSubject$.next([{ id: NotificationBannerIdEnum.CustomerResearch, rank: 11 }]);
                this.notificationBannerSubject$.next(true);
            }
        });

        this.subscription.add(subscriptionRef);
    }

    private validateClaimsYearsDocument(scenarios: ScenarioInterface[]): void {
        const scenarioFound = scenarios.find((scenario) => scenario.amount === 0);

        if (!this.isClaimYears) {
            return;
        }

        if (scenarioFound) {
            this.notificationsSubject$.next([{ id: NotificationBannerIdEnum.ClaimYearsInsuranceCoverage, rank: 1 }]);
            this.notificationBannerSubject$.next(true);
        }
    }

    private validateClaimsYearsCoverage(scenarios: ScenarioInterface[]): void {
        const scenarioFound = scenarios.find((scenario) => scenario.amount !== 0);

        if (!this.isClaimYears) {
            return;
        }

        if (scenarioFound) {
            this.notificationsSubject$.next([{ id: NotificationBannerIdEnum.ClaimYearsInsuranceUpload, rank: 1 }]);
            this.notificationBannerSubject$.next(true);
        }
    }

    private setTransferCookie(count = 1): void {
        this.storage.setItem(TRANSFER_KEY, count.toString(), { expires: +this.expireDate });
    }

    private validateTransferCookie(scenarios: ScenarioInterface[]): void {
        if (scenarios.length > 0) {
            return;
        }

        if (this.isTransfer) {
            this.setTransferCookie(0);
        }
    }

    private validateTransferPurchasedProducts(
        scenarios: ScenarioInterface[],
        purchasedProducts: PurchasedProductInterface[]
    ): void {
        if (scenarios.length > 0) {
            return;
        }

        if (this.isTransfer && purchasedProducts.length > 0) {
            this.notificationsSubject$.next([{ id: NotificationBannerIdEnum.InsuranseStartdate, rank: 10 }]);
            this.notificationBannerSubject$.next(true);
        } else {
            this.notificationBannerSubject$.next(false);
        }
    }

    private filterDuplicateNotifications(
        notifications: { id: NotificationBannerIdEnum; rank: number }[]
    ): { id: NotificationBannerIdEnum; rank: number }[] {
        const _notifications = notifications.map((notification) => JSON.stringify(notification));
        const _filteredNotifications = [...new Set(_notifications)];

        return _filteredNotifications.map((notification) => JSON.parse(notification));
    }

    private sortNotifications(
        notifications: { id: NotificationBannerIdEnum; rank: number }[]
    ): { id: NotificationBannerIdEnum; rank: number }[] {
        return notifications.sort((a, b) => a.rank - b.rank);
    }
}
